// History-API router with lazy views, guards, cancellation and focus management.

export function compilePath(path) {
  const keys = [];
  const pattern = path
    .replace(/\/+$/, '')
    .replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    .replace(/:(\w+)/g, (_, key) => { keys.push(key); return '([^/]+)'; });
  return { regex: new RegExp(`^${pattern || ''}/?$`), keys };
}

/** Pure matcher (unit-tested). Returns { route, params } or null. */
export function matchRoute(routes, pathname) {
  const clean = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  for (const route of routes) {
    route._compiled ??= compilePath(route.path);
    const m = route._compiled.regex.exec(clean);
    if (m) {
      const params = {};
      route._compiled.keys.forEach((k, i) => { params[k] = decodeURIComponent(m[i + 1]); });
      return { route, params };
    }
  }
  return null;
}

export function createRouter({ routes, outlet, guard, onRouteChange, renderLoading, renderNotFound, renderForbidden, renderError }) {
  let current = null;       // { destroy, controller }
  let navToken = 0;

  async function resolve() {
    const token = ++navToken;
    const url = new URL(window.location.href);
    const match = matchRoute(routes, url.pathname);

    current?.controller.abort();
    try { current?.destroy?.(); } catch (e) { console.error(e); }
    current = null;

    if (!match) {
      onRouteChange?.({ route: null, params: {}, url });
      renderNotFound(outlet);
      return finish('Page not found');
    }

    const { route, params } = match;
    const verdict = guard ? guard(route) : true;
    if (verdict !== true) {
      if (typeof verdict === 'string' && verdict.startsWith('/')) return navigate(verdict, { replace: true });
      onRouteChange?.({ route, params, url });
      renderForbidden(outlet);
      return finish('Access denied');
    }

    onRouteChange?.({ route, params, url });
    renderLoading(outlet);
    const controller = new AbortController();
    try {
      const mod = await route.view();
      if (token !== navToken) return; // a newer navigation won
      const handle = (await mod.render({ outlet, params, query: url.searchParams, signal: controller.signal, navigate, route })) ?? {};
      if (token !== navToken) { handle.destroy?.(); return; }
      current = { destroy: handle.destroy, controller };
      finish(route.title);
    } catch (error) {
      if (token !== navToken || controller.signal.aborted) return;
      console.error(error);
      renderError(outlet, error, () => resolve());
      finish('Error');
    }
  }

  function finish(title) {
    document.title = `${title} · NGW EMS`;
    outlet.focus({ preventScroll: true });
    window.scrollTo({ top: 0 });
  }

  function navigate(path, { replace = false } = {}) {
    if (replace) window.history.replaceState({}, '', path);
    else window.history.pushState({}, '', path);
    return resolve();
  }

  function onClick(event) {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const a = event.target.closest?.('a[href]');
    if (!a || a.target === '_blank' || a.hasAttribute('download') || a.dataset.external !== undefined) return;
    const url = new URL(a.href, window.location.href);
    if (url.origin !== window.location.origin || url.pathname.endsWith('.html')) return;
    event.preventDefault();
    if (url.pathname + url.search !== window.location.pathname + window.location.search) navigate(url.pathname + url.search);
  }

  return {
    start() {
      window.addEventListener('popstate', resolve);
      document.addEventListener('click', onClick);
      return resolve();
    },
    navigate,
    refresh: resolve,
  };
}
